import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Reveal, MaskLines } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/posts");
        setPosts(data);
      } catch (e) {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <section className="bg-cream grain pt-40 pb-16 md:pt-52 md:pb-20 relative">
        <div className="container-editorial relative z-10 max-w-4xl">
          <Reveal>
            <p className="eyebrow text-amber-deep mb-8 flex items-center gap-3">
              <span className="w-10 h-px bg-amber-deep" /> Insights
            </p>
          </Reveal>
          <h1 className="font-serif font-bold text-5xl md:text-7xl lg:text-8xl text-blue leading-[0.98] tracking-tight">
            <MaskLines lines={["Notes from"]} delay={0.1} />
            <MaskLines lines={["the field."]} className="italic text-amber-deep" delay={0.26} />
          </h1>
          <Reveal delay={0.5}>
            <p className="font-body text-lg text-charcoal/75 mt-8 max-w-2xl leading-relaxed">
              Short, practical thinking on building, funding, and exiting a business, drawn from the
              work itself.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream pb-24 md:pb-32">
        <div className="container-editorial">
          {loading ? (
            <div className="py-20 flex justify-center">
              <Loader2 className="animate-spin text-blue" size={22} />
            </div>
          ) : posts.length === 0 ? (
            <p data-testid="insights-empty" className="font-body text-charcoal/60 py-16">
              New insights are on the way. Check back soon.
            </p>
          ) : (
            <div className="divide-y divide-blue/12 border-t border-blue/12">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05}>
                  <Link
                    to={`/insights/${p.slug}`}
                    data-testid="insight-card"
                    className="group grid md:grid-cols-[180px_1fr_auto] gap-4 md:gap-10 items-baseline py-9 md:py-11"
                  >
                    <p className="eyebrow text-[0.6rem] text-slate">{fmtDate(p.created_at)}</p>
                    <div>
                      <h2 className="font-serif font-bold text-3xl md:text-4xl text-blue leading-tight group-hover:text-amber-deep transition-colors">
                        {p.title}
                      </h2>
                      {p.excerpt ? (
                        <p className="font-body text-charcoal/70 mt-3 max-w-2xl leading-relaxed">{p.excerpt}</p>
                      ) : null}
                    </div>
                    <ArrowUpRight
                      size={26}
                      className="text-blue/40 group-hover:text-amber-deep group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                    />
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </>
  );
}
